import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, X, Search } from 'lucide-react';

interface SearchableSelectOption {
	value: string | number;
	label: string;
}

interface SearchableSelectProps {
	options: SearchableSelectOption[];
	value?: string | number;
	onChange: (value: string | number) => void;
	placeholder?: string;
	searchPlaceholder?: string;
	className?: string;
	disabled?: boolean;
	error?: string;
	allowClear?: boolean;
	'aria-label'?: string;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
	options,
	value,
	onChange,
	placeholder = 'Select an option',
	searchPlaceholder = 'Search...',
	className = '',
	disabled = false,
	error,
	allowClear = true,
	'aria-label': ariaLabel
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');
	const [highlightedIndex, setHighlightedIndex] = useState(-1);
	const containerRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const listRef = useRef<HTMLDivElement>(null);

	const selectedOption = options.find(opt => opt.value === value);

	// Filter options based on search term
	const filteredOptions = options.filter(option =>
		option.label.toLowerCase().includes(searchTerm.toLowerCase())
	);

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
				setIsOpen(false);
				setSearchTerm('');
				setHighlightedIndex(-1);
			}
		};

		if (isOpen) {
			document.addEventListener('mousedown', handleClickOutside);
			// Focus search input when dropdown opens
			setTimeout(() => inputRef.current?.focus(), 0);
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [isOpen]);

	// Handle keyboard navigation
	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (!isOpen) {
			if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
				e.preventDefault();
				setIsOpen(true);
			}
			return;
		}

		switch (e.key) {
			case 'ArrowDown':
				e.preventDefault();
				setHighlightedIndex(prev => 
					prev < filteredOptions.length - 1 ? prev + 1 : prev
				);
				break;
			case 'ArrowUp':
				e.preventDefault();
				setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
				break;
			case 'Enter':
				e.preventDefault();
				if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
					handleSelect(filteredOptions[highlightedIndex].value);
				}
				break;
			case 'Escape':
				e.preventDefault();
				setIsOpen(false);
				setSearchTerm('');
				setHighlightedIndex(-1);
				break;
		}
	};

	const handleSelect = (selectedValue: string | number) => {
		onChange(selectedValue);
		setIsOpen(false);
		setSearchTerm('');
		setHighlightedIndex(-1);
	};

	const handleClear = (e: React.MouseEvent) => {
		e.stopPropagation();
		onChange('');
		setSearchTerm('');
	};

	// Scroll highlighted item into view
	useEffect(() => {
		if (highlightedIndex >= 0 && listRef.current) {
			const highlightedElement = listRef.current.children[highlightedIndex] as HTMLElement;
			if (highlightedElement) {
				highlightedElement.scrollIntoView({ block: 'nearest' });
			}
		}
	}, [highlightedIndex]);

	return (
		<div ref={containerRef} className={`relative ${className}`}>
			{/* Trigger Button */}
			<button
				type="button"
				onClick={() => !disabled && setIsOpen(!isOpen)}
				onKeyDown={handleKeyDown}
				disabled={disabled}
				aria-label={ariaLabel || placeholder}
				aria-expanded={isOpen}
				aria-haspopup="listbox"
				className={`w-full flex items-center justify-between px-3 py-2 border rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-accent-color focus:border-accent-color ${
					error
						? 'border-red-500 dark:border-red-500'
						: 'border-gray-300 dark:border-gray-600'
				} ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
			>
				<span className={`truncate ${selectedOption ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'}`}>
					{selectedOption ? selectedOption.label : placeholder}
				</span>
				<div className="flex items-center gap-2 shrink-0">
					{allowClear && value !== undefined && value !== '' && !disabled && (
						<X
							className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
							onClick={handleClear}
							aria-label="Clear selection"
						/>
					)}
					<ChevronDown
						className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'transform rotate-180' : ''}`}
					/>
				</div>
			</button>

			{/* Dropdown */}
			{isOpen && (
				<div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-hidden">
					{/* Search Input */}
					<div className="p-2 border-b border-gray-200 dark:border-gray-700">
						<div className="relative">
							<Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
							<input
								ref={inputRef}
								type="text"
								placeholder={searchPlaceholder}
								value={searchTerm}
								onChange={(e) => {
									setSearchTerm(e.target.value);
									setHighlightedIndex(-1);
								}}
								onKeyDown={handleKeyDown}
								className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-accent-color"
							/>
						</div>
					</div>

					{/* Options List */}
					<div
						ref={listRef}
						className="overflow-y-auto max-h-48"
					>
						{filteredOptions.length === 0 ? (
							<div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400 text-center">
								No options found
							</div>
						) : (
							filteredOptions.map((option, index) => (
								<button
									key={option.value}
									type="button"
									onClick={() => handleSelect(option.value)}
									onMouseEnter={() => setHighlightedIndex(index)}
									className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${
										option.value === value
											? 'bg-accent-color/10 text-accent-color font-medium'
											: 'text-gray-900 dark:text-gray-100'
									} ${
										index === highlightedIndex
											? 'bg-gray-100 dark:bg-gray-700'
											: ''
									}`}
								>
									{option.label}
								</button>
							))
						)}
					</div>
				</div>
			)}

			{/* Error Message */}
			{error && (
				<p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>
			)}
		</div>
	);
};

export default SearchableSelect;

